"use client";

import Link from "next/link";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useState } from "react";

export default function ProWhipHeader({ data }: any) {
    const router = useRouter();
    const pathname = usePathname();
    const [langOpen, setLangOpen] = useState(false);

    const menuItems = data?.menu?.children ?? [];
    const logo = data?.Logo?.media;
    const { enableLanguageSwitcher } = data || {};

    /* -------------------------
     * Detect current language
     * ------------------------- */
    const searchParams = useSearchParams();

    const localeParam = searchParams.get("locale") || "en_US";

    const currentLang: "en" | "kr" = localeParam === "ko_KR" ? "kr" : "en";

    const localeKey = localeParam;

    /* -------------------------
     * Language switch handler
     * ------------------------- */
    const switchLanguage = (lang: "en" | "kr") => {
        const newLocale = lang === "kr" ? "ko_KR" : "en_US";

        const params = new URLSearchParams(searchParams.toString());
        params.set("locale", newLocale);

        router.push(`${pathname}?${params.toString()}`);
        setLangOpen(false);
    };

    /* -------------------------
     * Menu visibility (unchanged)
     * ------------------------- */
    const isMenuItemVisible = (item: any) =>
        item?.configuration?.displayHighlightMenuItem !== false;

    /* -------------------------
     * Helper: attach locale to any URL
     * ------------------------- */
    const withLocale = (url: string) => {
        const params = new URLSearchParams(searchParams.toString());
        params.set("locale", localeKey);
        return `${url}?${params.toString()}`;
    };    

    return (
        <>
            {/* BLACK BAR */}
            <div
                style={{
                    position: "fixed",
                    top: "20px",
                    left: 0,
                    width: "100%",
                    height: "90px",
                    backgroundColor: "#000",
                    zIndex: 10000,
                }}
            />

            {/* HEADER */}
            <header
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "72px",
                    zIndex: 10001,
                    backgroundColor: "#000",
                }}
            >
                <div className="mx-auto max-w-[1440px] flex items-center justify-between px-16 h-full text-white pt-10">

                    {/* LOGO */}
                        <Link href={withLocale("/en/prowhip-home")}>
                        <span className="flex items-center cursor-pointer ml-6">
                            {logo && (
                                <img
                                    src={logo.file}
                                    alt={logo.alt || "ProWhip"}
                                    className="h-14 w-auto"
                                />
                            )}
                        </span>
                    </Link>

                    {/* MENU + LANGUAGE SWITCHER */}
                    <nav className="flex items-center gap-8 text-lg font-small ml-16 px-6">
                        {menuItems
                            .filter(isMenuItemVisible)
                            .map((item: any) => (
                                <div
                                    key={item.pageFolderId}
                                    className="relative group"
                                >
                                    <Link href={withLocale(item._url)}>
                                        <span className="cursor-pointer py-2 flex items-center gap-1">
                                            {/*item.name*/}
                                            {item?.configuration?.seoTitle?.[localeKey] ?? item?.name}
                                            {item.children?.filter(isMenuItemVisible).length > 0 && (
                                                <span className="text-xs">▼</span>
                                            )}
                                        </span>
                                    </Link>

                                    {/* SUB MENU */}
                                    {item.children?.filter(isMenuItemVisible).length > 0 && (
                                        <div
                                            className="
                                                absolute
                                                left-0
                                                top-full
                                                hidden
                                                min-w-[220px]
                                                bg-white
                                                text-black
                                                shadow-lg
                                                border
                                                border-gray-200
                                                group-hover:block
                                            "
                                        >
                                            {item.children
                                                .filter(isMenuItemVisible)
                                                .map((child: any) => (
                                                    <Link
                                                        key={child.pageFolderId}
                                                        href={withLocale(child._url)}
                                                    >
                                                        <span
                                                            style={{ color: "#000" }}
                                                            className="block px-5 py-3 hover:bg-gray-100 cursor-pointer"
                                                        >
                                                            {/*child.name*/}
                                                            {child?.configuration?.seoTitle?.[localeKey] ?? child?.name}
                                                        </span>
                                                    </Link>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                        {/* LANGUAGE SWITCHER DROPDOWN */}
                        {enableLanguageSwitcher && (
                            <div className="relative ml-8">
                                <button
                                    onClick={() => setLangOpen(!langOpen)}
                                    className="flex items-center gap-1 text-lg font-small cursor-pointer"
                                >
                                    {currentLang === "en" ? "English" : "한국어"}
                                    <span className="text-xs">▾</span>
                                </button>

                                {langOpen && (
                                    <div className="absolute right-0 top-full mt-3 bg-white text-black min-w-[140px] shadow-lg border border-gray-200">
                                        <button style={{ color: "#000" }}
                                            onClick={() => switchLanguage("en")}
                                            className="block w-full text-left px-5 py-3 hover:bg-gray-100"
                                        >
                                            English
                                        </button>
                                        <button style={{ color: "#000" }}
                                            onClick={() => switchLanguage("kr")}
                                            className="block w-full text-left px-5 py-3 hover:bg-gray-100"
                                        >
                                            한국어
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </nav>
                </div>
            </header>

            {/* SPACER */}
            <div style={{ height: "72px" }} />
        </>
    );
}
